<template>
    <main-dialog
        ref="dialog"
        heading="parkingLot.allocatePromotionToParkingLot.heading"
        btn-confirm-text="actions.move"
        :has-activator="hasActivator"
        :confirm-btn-disabled="isDisabled"
        @confirm="allocatePromotionsDialogConfirm"
        @close="closeDialog"
    >
        <template v-if="hasActivator" v-slot:actions="{ activator }">
            <icon v-bind="activator" icon-name="duplicate" small @click.stop />
        </template>
        <template v-slot:default>
            <div class="allocate-promotion">
                <p class="allocate-promotion__description">
                    {{ toSentenceCase($t('parkingLot.allocatePromotionToParkingLot.description')) }}
                </p>
                <v-text-field
                    class="rtls-text-field rtls-text-field--grey pb-2"
                    disabled
                    autocomplete="off"
                    :label="modalLabel"
                />
                <v-text-field
                    class="rtls-text-field rtls-text-field--grey pb-2"
                    disabled
                    autocomplete="off"
                    :label="campaign.name"
                />
                <v-text-field
                    class="rtls-text-field rtls-text-field--grey pb-2"
                    disabled
                    autocomplete="off"
                    :label="subCampaign.name"
                />
            </div>

            <div v-if="requestInProgress" class="in-progress--message">
                <label class="in-progress__message">{{
                    toSentenceCase($t('parkingLot.allocatePromotionToParkingLot.inProgressMessage'))
                }}</label>
                <spinner-dynamic />
            </div>
        </template>
    </main-dialog>
</template>

<script>
import to from 'await-to-js';
import { mapActions, mapGetters, mapState } from 'vuex';
import { get } from 'lodash';

export default {
    props: {
        hasActivator: {
            type: Boolean,
            default: true,
        },
        campaign: {
            type: Object,
            required: true,
        },
        subCampaign: {
            type: Object,
            required: true,
        },
        scenario: {
            type: Object,
            required: true,
        },
        startDate: {
            type: String,
            required: true,
        },
        endDate: {
            type: String,
            required: true,
        },
        promotionIds: {
            type: Array,
            required: true,
        },
    },
    emits: ['confirm'],
    data() {
        return {
            isDisabled: false,
        };
    },
    computed: {
        ...mapState('promotions', ['loading']),
        ...mapGetters('weeks', ['getWeekByDate']),
        requestInProgress() {
            return this.loading;
        },
        modalLabel() {
            const startWeek = this.weekNumberShorthandClient(
                get(this.getWeekByDate(this.scenario.startDate), 'weekOfYear')
            );
            const lastWeek = this.weekNumberShorthandClient(
                get(this.getWeekByDate(this.scenario.endDate), 'weekOfYear')
            );
            const startDate = this.dateShorthand(this.scenario.startDate);
            const endDate = this.dateShorthand(this.scenario.endDate);
            return `${startWeek} - ${lastWeek} | ${startDate} - ${endDate}`;
        },
    },
    methods: {
        ...mapActions('promotions', ['movePromotionsToParkingLot']),
        openDialog() {
            this.$refs.dialog.open();
        },
        closeDialog() {
            this.$refs.dialog.close();
        },
        async allocatePromotionsDialogConfirm() {
            this.isDisabled = true;
            const [error] = await to(
                this.movePromotionsToParkingLot({
                    promotionIds: this.promotionIds,
                    startDate: this.startDate,
                    endDate: this.endDate,
                })
            );

            if (!error) {
                this.closeDialog();
            }
            this.isDisabled = false;
            this.$emit('confirm');
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.allocate-promotion {
    font-size: 1.2rem;

    &__description {
        padding: 1rem 0;
    }
}

.in-progress__message {
    font-size: 1.3rem;
    color: $promo-text-colour;
    margin: 1rem;

    .spinner {
        width: 1.4rem;
        height: 1.4rem;
    }
}

:deep(.v-text-field__slot) {
    label {
        padding-left: 1rem;
    }
}
</style>
